import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, Divider, InputAdornment, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DiscountIcon from '@mui/icons-material/Discount';
import PercentIcon from '@mui/icons-material/Percent';
import CallMadeIcon from '@mui/icons-material/CallMade';
import LinkIcon from '@mui/icons-material/Link';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import BuildIcon from '@mui/icons-material/Build';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../reduxStore/Store';
import { fetchLignesByDocumentId, setSelectedLigne } from '../reduxStore/LigneDocumentVenteSlice';
import { setSelectedDocument } from '../reduxStore/documentVenteSlice';
import ImportLignesDialog from '../components/Forms/ImportLignesDialog';
import MyTable from '../components/MyTable';
import { TableAction, TableHeader } from '../constants/interfaces';
import { Document_vente, Ligne_Document_Vente } from '../models/models';
import { ButtonType, PERMISSION_PRIX_REVIENT, TypeLigne } from '../constants/enums';
import { usePermission } from '../hooks/usePermission';
import { formatNumberWithSpaces } from '../utils/utils';
import { formatQuantite } from '../utils/quantiteUtils';
import { computeCoutFabrication } from '../utils/coutFabrication';
import { computeValeurInventaire } from '../utils/valeurInventaire';
import api from '../reduxStore/api';

// ── Couleur / libellé badge par type ──────────────────────────────────────

const TYPE_CHIP: Record<TypeLigne, { label: string; color: 'default' | 'error' | 'warning' | 'info' | 'success' | 'secondary' | 'primary' }> = {
  [TypeLigne.ARTICLE]:        { label: 'Article',        color: 'default'   },
  [TypeLigne.REMISE]:         { label: 'Remise',         color: 'error'     },
  [TypeLigne.CHARGE]:         { label: 'Frais',          color: 'warning'   },
  [TypeLigne.COMMENTAIRE]:    { label: 'Commentaire',    color: 'info'      },
  [TypeLigne.COMPOSANT]:      { label: 'Composant',      color: 'success'   },
  [TypeLigne.IMMOBILISATION]: { label: 'Immobilisation', color: 'secondary' },
  [TypeLigne.TRANSFERT]:      { label: 'Transfert',      color: 'primary'   },
  [TypeLigne.AJUSTEMENT]:     { label: 'Ajustement',     color: 'warning'   },
};

// ── Libellé de la ligne (article ou libelle_libre) ────────────────────────
// ARTICLE et COMPOSANT pointent tous deux vers un article réel → utiliser article.designation
// REMISE / CHARGE / COMMENTAIRE → libellé libre

const LIGNES_AVEC_ARTICLE = [TypeLigne.ARTICLE, TypeLigne.COMPOSANT];

function getLigneLabel(row: Ligne_Document_Vente): string {
  const type = row.type_ligne ?? TypeLigne.ARTICLE;
  if (!LIGNES_AVEC_ARTICLE.includes(type))
    return row.libelle_libre ?? TYPE_CHIP[type].label;
  return row.article?.designation ?? 'N/A';
}

function getLigneCode(row: Ligne_Document_Vente): string {
  const type = row.type_ligne ?? TypeLigne.ARTICLE;
  if (!LIGNES_AVEC_ARTICLE.includes(type)) return '—';
  return row.article?.code ?? 'N/A';
}

// ═════════════════════════════════════════════════════════════════════════

interface LigneDocumentVenteProps {
  /** Optionnel : appelé au clic "Modifier" d'une ligne. Si fourni, remplace
   *  le dispatch direct de setSelectedLigne. Permet au parent de garantir
   *  l'ouverture du dialog même quand on re-clique la même ligne (sinon
   *  la référence inchangée empêche le useEffect parent de fire). */
  onEdit?: (ligne: Ligne_Document_Vente) => void;
}

const LigneDocumentVente: React.FC<LigneDocumentVenteProps> = ({ onEdit }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { ligneDOcData, total } = useSelector((state: any) => state.LigneDocumentVenteSlice);
  const { selectedDocument }   = useSelector((state: any) => state.DocumentVenteSlice);

  const [fetchParams, setFetchParams] = useState({
    limit: 1000,
    offset: 0,
    document_vente_id: selectedDocument?.id ?? -1,
  });

  const [childDocs, setChildDocs] = useState<Document_vente[]>([]);
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => {
    if (selectedDocument) {
      setFetchParams(prev => ({ ...prev, document_vente_id: selectedDocument.id }));
    }
  }, [selectedDocument]);

  useEffect(() => {
    // Pas de document sélectionné → aucun appel : les droits des lignes sont ceux du
    // document parent, le backend refuse une requête sans document identifiable.
    if (fetchParams.document_vente_id <= 0) return;
    dispatch(fetchLignesByDocumentId(fetchParams));
  }, [dispatch, fetchParams]);

  const fetchChildren = useCallback(async () => {
    if (!selectedDocument?.id) return;
    try {
      const res = await api.get('/documentVentes/getAll', {
        params: { related_doc: selectedDocument.id, limit: 100, offset: 0 },
      });
      setChildDocs(res.data?.data ?? []);
    } catch { setChildDocs([]); }
  }, [selectedDocument?.id]);

  useEffect(() => { fetchChildren(); }, [fetchChildren]);

  // ── Pré-calculs utilisés à la fois par les headers et le récap ───────────
  // Déclarés ici pour rester accessibles aux renders inline des colonnes.
  // useMemo : référence stable → évite de refaire tourner les useMemo qui en dépendent
  // à chaque render (coutSynthese / valeurInventaire).
  const lignes: Ligne_Document_Vente[] = useMemo(() => ligneDOcData ?? [], [ligneDOcData]);
  const isFabrication = !!selectedDocument?.type_document?.est_fabrication;
  const isInventaire  = !!selectedDocument?.type_document?.est_inventaire;
  // Sans le droit de voir le coût (0156) : coûts de fabrication et valeur des écarts
  // d'inventaire non affichés (l'API renvoie les coûts à null)
  const voitCout      = usePermission(PERMISSION_PRIX_REVIENT);

  // Inventaire : synthèse informative des écarts valorisés au coût (comme
  // computeCoutFabrication valorise les composants). null hors inventaire.
  //   parLigne : Map<id ligne ARTICLE parente, { ecart signé, valeur signée, coutUnitaire }>
  const valeurInventaire = useMemo(
    () => (isInventaire ? computeValeurInventaire(lignes) : null),
    [lignes, isInventaire],
  );

  // Synthèse coûts de fabrication (matière directe + frais/remises au prorata).
  // Calculée uniquement sur les docs `est_fabrication = 1` — null sinon, ce qui
  // permet aux composants visuels de la masquer entièrement.
  const coutSynthese = useMemo(
    () => (isFabrication && voitCout ? computeCoutFabrication(lignes) : null),
    [lignes, isFabrication, voitCout],
  );

  // Largeurs : MyTable est en `tableLayout: 'fixed'`, donc en POURCENTAGES — ils se
  // calculent sur la largeur du tableau et ne peuvent jamais la dépasser. Des largeurs
  // en pixels finissent par déborder dès que l'écran rétrécit (leur somme est absolue),
  // d'où le scroll horizontal. Ne pas y revenir.
  //
  // Le total fait 86 % et non 100 % : la colonne d'actions de MyTable est fixée à 180 px
  // et n'est pas pilotable depuis ici. Les 14 % restants la couvrent à partir d'environ
  // 1290 px de large ; en dessous, `overflowX: 'hidden'` du TableContainer rogne au lieu
  // de faire scroller. Si une colonne est ajoutée ou retirée, garder ce total à 86 %.
  const headers: TableHeader<Ligne_Document_Vente>[] = [
    {
      key: 'type_ligne',
      label: 'Type',
      // Doit contenir la puce la plus large (« Commentaire ») padding de cellule
      // compris : en dessous, la puce est coupée net par l'overflow hidden.
      width: '9%',
      render: (row) => {
        const type = row.type_ligne ?? TypeLigne.ARTICLE;
        if (type === TypeLigne.ARTICLE) return null;
        const chip = TYPE_CHIP[type];
        return <Chip label={chip.label} size="small" color={chip.color} variant="outlined" />;
      },
    },
    {
      // `key` sert d'identité de colonne pour la visibilité (MyTable filtre sur
      // String(h.key)). Deux colonnes lisant toutes deux `article` partageraient la
      // même identité : masquer l'une masquerait l'autre. Le code s'appuie donc sur
      // `article_id`, la désignation garde `article`. Les deux ont un `render`, la
      // clé n'est jamais utilisée pour lire la valeur.
      key: 'article_id',
      label: 'Code',
      width: '8%',
      render: (row) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
          {getLigneCode(row)}
        </Typography>
      ),
    },
    {
      key: 'article',
      label: 'Désignation / Libellé',
      width: '25%',
      render: (row) => {
        const type = row.type_ligne ?? TypeLigne.ARTICLE;
        const isSpecial = type !== TypeLigne.ARTICLE;
        const label = getLigneLabel(row);
        return (
          <Typography
            variant="body2"
            // Cette colonne a un `render`, donc MyTable ne l'enveloppe pas dans
            // OverflowText : sans ellipse ni `title` ici, un libellé long est coupé
            // net et devient illisible, sans moyen de lire la fin.
            title={label}
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontStyle: isSpecial ? 'italic' : 'normal',
              color: type === TypeLigne.REMISE ? 'error.main'
                   : type === TypeLigne.CHARGE ? 'warning.main'
                   : type === TypeLigne.COMMENTAIRE ? 'info.main'
                   : 'text.primary',
            }}
          >
            {label}
          </Typography>
        );
      },
    },
    {
      key: 'quantite',
      label: isInventaire ? 'Qté comptée' : 'Quantité',
      align: 'right',
      width: '7%',
      render: (row) => {
        const type = row.type_ligne ?? TypeLigne.ARTICLE;
        if (type === TypeLigne.COMMENTAIRE) return null;
        // Ligne AJUSTEMENT : quantité signée (entrée +, sortie −) pour la lisibilité du journal.
        if (type === TypeLigne.AJUSTEMENT) {
          const q = Number(row.quantite ?? 0) * Number(row.signe ?? 0);
          return (
            <Typography variant="body2" sx={{ fontWeight: 600, color: q >= 0 ? 'success.main' : 'error.main' }}>
              {q > 0 ? `+${formatQuantite(q, row.article)}` : formatQuantite(q, row.article)}
            </Typography>
          );
        }
        const qte = <span>{formatQuantite(row.quantite, row.article)}</span>;

        // Indicateur de coût de fabrication — visible seulement sur les docs fabrication
        // et seulement pour les lignes ARTICLE (le produit fini, pas les composants).
        const coutLigne = (isFabrication && type === TypeLigne.ARTICLE && row.id != null)
          ? coutSynthese?.parLigne.get(row.id)
          : null;
        if (!coutLigne) return qte;

        return (
          <Box display="inline-flex" alignItems="center" gap={0.5}>
            {qte}
            <Tooltip
              arrow
              placement="left"
              title={
                <Box sx={{ fontSize: 12, lineHeight: 1.6 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                    Coût estimé de fabrication
                  </Typography>
                  <Box display="flex" justifyContent="space-between" gap={2}>
                    <span>Matière directe</span>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumberWithSpaces(coutLigne.coutMatiereDirect)}</span>
                  </Box>
                  {Math.abs(coutLigne.partAjustement) > 0.001 && (
                    <Box display="flex" justifyContent="space-between" gap={2}>
                      <span>Frais / remises alloués</span>
                      <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {coutLigne.partAjustement > 0 ? '+' : ''}{formatNumberWithSpaces(coutLigne.partAjustement)}
                      </span>
                    </Box>
                  )}
                  <Divider sx={{ my: 0.5, borderColor: 'rgba(255,255,255,0.3)' }} />
                  <Box display="flex" justifyContent="space-between" gap={2}>
                    <span style={{ fontWeight: 700 }}>Coût complet</span>
                    <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                      {formatNumberWithSpaces(coutLigne.coutComplet)}
                    </span>
                  </Box>
                  <Box display="flex" justifyContent="space-between" gap={2} sx={{ color: 'rgba(255,255,255,0.75)' }}>
                    <span>Coût unitaire</span>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumberWithSpaces(coutLigne.coutUnitaire)}</span>
                  </Box>
                </Box>
              }
            >
              <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
            </Tooltip>
          </Box>
        );
      },
    },
    {
      key: 'prix_unitaire',
      label: 'Prix unit.',
      align: 'right',
      width: '7%',
      render: (row) => {
        const type = row.type_ligne ?? TypeLigne.ARTICLE;
        if (type === TypeLigne.COMMENTAIRE) return null;
        return (
          <Typography
            variant="body2"
            sx={{ color: row.prix_unitaire < 0 ? 'error.main' : 'inherit' }}
          >
            {formatNumberWithSpaces(row.prix_unitaire)}
          </Typography>
        );
      },
    },
    {
      key: 'remise',
      label: 'Remise %',
      align: 'right',
      width: '5%',
      render: (row) => {
        const type = row.type_ligne ?? TypeLigne.ARTICLE;
        if (type !== TypeLigne.ARTICLE || !row.remise) return null;
        return <span>{formatNumberWithSpaces(row.remise)}</span>;
      },
    },
    {
      key: 'tva',
      label: 'TVA %',
      align: 'right',
      width: '5%',
      render: (row) => {
        const type = row.type_ligne ?? TypeLigne.ARTICLE;
        if (type === TypeLigne.COMMENTAIRE) return null;
        return <span>{formatNumberWithSpaces(row.tva ?? null)}</span>;
      },
    },
    {
      key: 'total_ttc',
      label: 'Total TTC',
      align: 'right',
      width: '9%',
      render: (row) => {
        const type = row.type_ligne ?? TypeLigne.ARTICLE;
        if (type === TypeLigne.COMMENTAIRE) return null;
        return (
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: (row.total_ttc ?? 0) < 0 ? 'error.main' : 'inherit',
            }}
          >
            {formatNumberWithSpaces(row.total_ttc ?? null)}
          </Typography>
        );
      },
    },
    {
      key: 'depot',
      label: 'Dépôt',
      width: '7%',
      render: (row) => {
        const type = row.type_ligne ?? TypeLigne.ARTICLE;
        // ARTICLE + lignes de mouvement stock (COMPOSANT = sortie, TRANSFERT = sortie source)
        const afficheDepot = type === TypeLigne.ARTICLE
          || type === TypeLigne.COMPOSANT
          || type === TypeLigne.TRANSFERT;
        if (!afficheDepot) return null;
        return <span>{row.depot?.nom ?? '—'}</span>;
      },
    },
    // Souvent vide, et tronquée proprement par OverflowText (ellipse + infobulle)
    // puisqu'elle n'a pas de `render` : elle peut céder de la place aux autres.
    { key: 'extra_note', label: 'Note', width: '4%' },
  ];

  // Inventaire : colonne « Écart » (comptée − théorique) juste après la quantité, dérivée
  // de la ligne AJUSTEMENT enfant. Affiche l'écart en quantité + sa valeur au coût
  // (informatif, comme le coût de fabrication). prix / remise / tva n'ont pas de sens ici.
  if (isInventaire) {
    const qIdx = headers.findIndex(h => h.key === 'quantite');
    headers.splice(qIdx + 1, 0, {
      key: 'ecart_inventaire',
      label: 'Écart / Valeur',
      align: 'right',
      width: '11%',
      render: (row) => {
        if ((row.type_ligne ?? TypeLigne.ARTICLE) !== TypeLigne.ARTICLE || row.id == null) return null;
        const d = valeurInventaire?.parLigne.get(row.id);
        if (!d || d.ecart === 0) return <Typography variant="body2" color="text.disabled">0</Typography>;
        const col = d.ecart > 0 ? 'success.main' : 'error.main';
        return (
          <Box sx={{ lineHeight: 1.2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: col }}>
              {d.ecart > 0 ? `+${formatNumberWithSpaces(d.ecart)}` : formatNumberWithSpaces(d.ecart)}
            </Typography>
            {voitCout && (
              <Typography variant="caption" sx={{ color: col, fontVariantNumeric: 'tabular-nums' }}>
                {d.valeur > 0 ? `+${formatNumberWithSpaces(d.valeur)}` : formatNumberWithSpaces(d.valeur)}
              </Typography>
            )}
          </Box>
        );
      },
    });
  }

  // cf. note dans DocumentVente.tsx — comparaison truthy pour tolérer les variations
  // de type (Number/BigInt/Boolean) issues du driver mariadb selon la SP.
  const isDocCloture = !!selectedDocument?.cloture;

  const tableActions: TableAction<Ligne_Document_Vente>[] = [
    {
      label: 'Modifier',
      callback: (ligne) => onEdit ? onEdit(ligne) : dispatch(setSelectedLigne(ligne)),
      type: ButtonType.EDIT,
      // Les lignes COMPOSANT (nomenclature), TRANSFERT (contrepartie de sortie source) et
      // AJUSTEMENT (écart d'inventaire) sont auto-générées → non éditables/supprimables
      // directement (gérées par la ligne ARTICLE parente). Un document clôturé est
      // verrouillé → aucune édition de ligne.
      hidden: (ligne) => ligne.type_ligne === TypeLigne.COMPOSANT
                      || ligne.type_ligne === TypeLigne.TRANSFERT
                      || ligne.type_ligne === TypeLigne.AJUSTEMENT
                      || isDocCloture,
    },
  ];

  const handlePageChange = (newPage: number, newRowsPerPage: number) => {
    setFetchParams(prev => ({
      ...prev,
      offset: newPage * newRowsPerPage,
      limit: newRowsPerPage,
    }));
  };

  // ── Filtre par type de ligne (chips toggle, persisté en localStorage) ────
  const HIDDEN_TYPES_KEY = 'lignedoc.hidden_types';
  const TYPES_FILTRABLES: TypeLigne[] = [
    TypeLigne.ARTICLE, TypeLigne.REMISE, TypeLigne.CHARGE, TypeLigne.COMMENTAIRE, TypeLigne.COMPOSANT, TypeLigne.TRANSFERT, TypeLigne.AJUSTEMENT,
  ];
  const [hiddenTypes, setHiddenTypes] = useState<TypeLigne[]>(() => {
    try {
      const raw = localStorage.getItem(HIDDEN_TYPES_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const toggleType = (t: TypeLigne) => {
    setHiddenTypes(prev => {
      const next = prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t];
      try { localStorage.setItem(HIDDEN_TYPES_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  // ── Recherche texte (insensible à la casse, sur plusieurs champs) ────────
  const [search, setSearch] = useState('');

  // ── Calculs récapitulatifs (sur TOUTES les lignes, jamais filtrées) ──────
  // `lignes` et `isFabrication` sont déclarés en haut du composant (accessibles
  // aux renders de colonnes). Ne pas les redéclarer ici.

  // Lignes affichées dans le tableau (hors hiddenTypes + match search)
  const searchLower = search.trim().toLowerCase();
  const lignesAffichees = lignes
    .filter(l => !hiddenTypes.includes(l.type_ligne ?? TypeLigne.ARTICLE))
    .filter(l => {
      if (!searchLower) return true;
      const fields = [
        l.article?.code,
        l.article?.designation,
        l.libelle_libre,
        l.extra_note,
        l.depot?.nom,
      ];
      return fields.some(v => v && String(v).toLowerCase().includes(searchLower));
    });

  const lignesArticle = lignes.filter(l => !l.type_ligne || l.type_ligne === TypeLigne.ARTICLE);
  const lignesCharge  = lignes.filter(l => l.type_ligne === TypeLigne.CHARGE);
  const lignesRemise  = lignes.filter(l => l.type_ligne === TypeLigne.REMISE);

  const totalQte      = lignesArticle.reduce((s, l) => s + (l.quantite    ?? 0), 0);
  const totalHTArt    = lignesArticle.reduce((s, l) => s + (l.total_ht    ?? 0), 0);
  const totalTTCArt   = lignesArticle.reduce((s, l) => s + (l.total_ttc   ?? 0), 0);
  const totalCharges  = lignesCharge.reduce ((s, l) => s + (l.total_ttc   ?? 0), 0);
  const totalRemises  = lignesRemise.reduce ((s, l) => s + (l.total_ttc   ?? 0), 0);

  // Sur un doc de fabrication, le total commercial = ARTICLE (produit fini) uniquement.
  // Les COMPOSANT (matière consommée valorisée) sont des inputs coût, pas de la valeur de sortie.
  // CHARGE / REMISE / COMMENTAIRE sont également exclus dans ce contexte.
  // Doit rester aligné avec le SUM côté SP (migration 0033).
  const lignesPourTotal = lignes.filter(l => {
    const t = l.type_ligne ?? TypeLigne.ARTICLE;
    if (t === TypeLigne.COMMENTAIRE) return false;
    if (isFabrication) return t === TypeLigne.ARTICLE;
    return t !== TypeLigne.COMPOSANT;
  });

  // TVA ventilée — limitée aux lignes prises en compte dans le total général.
  const tvaMap: Record<string, number> = {};
  for (const l of lignesPourTotal) {
    const taux = String(l.tva ?? 0);
    tvaMap[taux] = (tvaMap[taux] ?? 0) + (l.total_ht ?? 0) * (Number(taux) / 100);
  }
  const tvaEntries = Object.entries(tvaMap).filter(([, v]) => Math.abs(v) > 0.001);

  // Toujours sommer les lignes du state — selectedDocument.total_ttc est un snapshot
  // serveur qui ne se rafraîchit pas pendant les mutations locales (ajout/suppression
  // d'une ligne sans refetch). La table affiche les lignes courantes, le total doit
  // refléter cette même source — sinon désync visible (ex: remise affichée mais non
  // déduite du Total général).
  const totalGeneral = lignesPourTotal.reduce((s, l) => s + (l.total_ttc ?? 0), 0);

  // ── Helpers affichage ─────────────────────────────────────────────────────

  const SummaryLabel = ({ children }: { children: React.ReactNode }) => (
    <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>
      {children}
    </Typography>
  );

  const SummaryRow = ({ label, value, bold = false, color, indent = false }: {
    label: string; value: number | null; bold?: boolean; color?: string; indent?: boolean;
  }) => (
    <Box display="flex" justifyContent="space-between" alignItems="center" py={0.4} px={indent ? 1 : 0}>
      <Typography variant="body2" sx={{ color: color ?? 'text.secondary', fontWeight: bold ? 600 : 400 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: bold ? 700 : 500, color: color ?? 'text.primary', minWidth: 120, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
        {formatNumberWithSpaces(value)}
      </Typography>
    </Box>
  );

  const handleImported = () => {
    setImportOpen(false);
    dispatch(fetchLignesByDocumentId(fetchParams));
  };

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" mb={1}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<FileUploadIcon />}
          onClick={() => setImportOpen(true)}
          disabled={!selectedDocument?.id || isDocCloture}
          title={isDocCloture ? "Document clôturé — non modifiable" : undefined}
        >
          Importer Excel
        </Button>
      </Box>

      <ImportLignesDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        documentVenteId={selectedDocument?.id ?? -1}
        depotId={selectedDocument?.depot_id ?? 0}
        onImported={handleImported}
      />

      {/* ── Filtres : chips par type + recherche texte ── */}
      <Box display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
          Filtrer :
        </Typography>
        {TYPES_FILTRABLES.map((t) => {
          const isVisible = !hiddenTypes.includes(t);
          return (
            <Chip
              key={t}
              label={TYPE_CHIP[t].label}
              color={TYPE_CHIP[t].color === 'default' ? 'primary' : TYPE_CHIP[t].color}
              variant={isVisible ? 'filled' : 'outlined'}
              size="small"
              onClick={() => toggleType(t)}
              sx={{ cursor: 'pointer', opacity: isVisible ? 1 : 0.55 }}
            />
          );
        })}
        <Box sx={{ flex: 1 }} />
        <TextField
          size="small"
          placeholder="Rechercher (code, désignation, note, dépôt...)"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 320 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <ClearIcon
                  fontSize="small"
                  sx={{ cursor: 'pointer', color: 'text.disabled' }}
                  onClick={() => setSearch('')}
                />
              </InputAdornment>
            ) : null,
          }}
        />
      </Box>

      <MyTable
        tableKey="ligne_document_vente"
        headers={headers}
        data={lignesAffichees}
        onPageChange={handlePageChange}
        actions={tableActions}
        totalRows={total}
        hidePagination
      />

      {/* ── Récapitulatif ── */}
      {lignes.length > 0 && (
        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Paper
            elevation={0}
            sx={{
              width: 360,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            {/* Articles */}
            <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Box display="flex" alignItems="center" gap={0.75}>
                  <ReceiptLongIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <SummaryLabel>Articles ({lignesArticle.length})</SummaryLabel>
                </Box>
                <Chip
                  label={`Qté : ${formatNumberWithSpaces(totalQte)}`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', height: 20 }}
                />
              </Box>
              <SummaryRow label="Total HT"  value={totalHTArt}  />
              <SummaryRow label="Total TTC" value={totalTTCArt} />
            </Box>

            {/* Ajustements */}
            {(lignesCharge.length > 0 || lignesRemise.length > 0) && (
              <>
                <Divider />
                <Box sx={{ px: 2, py: 1 }}>
                  <Box display="flex" alignItems="center" gap={0.75} mb={0.75}>
                    <DiscountIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <SummaryLabel>Ajustements</SummaryLabel>
                  </Box>
                  {lignesCharge.length > 0 && (
                    <SummaryRow
                      label={`Frais / Charges (${lignesCharge.length})`}
                      value={totalCharges}
                      color={totalCharges < 0 ? 'error.main' : 'warning.main'}
                    />
                  )}
                  {lignesRemise.length > 0 && (
                    <SummaryRow
                      label={`Remises (${lignesRemise.length})`}
                      value={totalRemises}
                      color="error.main"
                    />
                  )}
                </Box>
              </>
            )}

            {/* TVA */}
            {tvaEntries.length > 0 && (
              <>
                <Divider />
                <Box sx={{ px: 2, py: 1 }}>
                  <Box display="flex" alignItems="center" gap={0.75} mb={0.75}>
                    <PercentIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <SummaryLabel>TVA ventilée</SummaryLabel>
                  </Box>
                  {tvaEntries.map(([taux, montant]) => (
                    <SummaryRow key={taux} label={`TVA ${taux} %`} value={montant} indent />
                  ))}
                </Box>
              </>
            )}

            {/* Coûts de production — uniquement sur les documents de fabrication.
                La valeur fabriquée est la somme des lignes ARTICLE (= totalGeneral
                puisque sur un doc fabrication les autres types sont exclus du total). */}
            {isFabrication && coutSynthese && (
              <>
                <Divider />
                <Box sx={{ px: 2, py: 1 }}>
                  <Box display="flex" alignItems="center" gap={0.75} mb={0.75}>
                    <BuildIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <SummaryLabel>Coûts de production</SummaryLabel>
                  </Box>
                  <SummaryRow label="Coût matière directe" value={coutSynthese.coutMatiereTotal} />
                  {Math.abs(coutSynthese.totalCharges) > 0.001 && (
                    <SummaryRow
                      label="Frais alloués"
                      value={coutSynthese.totalCharges}
                      color={coutSynthese.totalCharges < 0 ? 'error.main' : 'warning.main'}
                    />
                  )}
                  {Math.abs(coutSynthese.totalRemises) > 0.001 && (
                    <SummaryRow label="Remises allouées" value={coutSynthese.totalRemises} color="error.main" />
                  )}
                  <Divider sx={{ my: 0.5 }} />
                  <SummaryRow label="Coût total estimé" value={coutSynthese.coutTotalDocument} bold />
                  {coutSynthese.valeurFabriqueeHT > 0 && (
                    <Box sx={{ mt: 0.75 }}>
                      <SummaryRow
                        label={`Marge estimée HT (${coutSynthese.margePct >= 0 ? '+' : ''}${coutSynthese.margePct.toFixed(1)} %)`}
                        value={coutSynthese.margeEstimeeHT}
                        bold
                        color={coutSynthese.margeEstimeeHT >= 0 ? 'success.main' : 'error.main'}
                      />
                    </Box>
                  )}
                </Box>
              </>
            )}

            {/* Valeur des ajustements — uniquement sur les documents d'inventaire.
                Informatif (comme les coûts de fabrication) : écarts valorisés au coût
                snapshoté (prix_revient), fallback prix_r article. */}
            {isInventaire && valeurInventaire && voitCout && (
              <>
                <Divider />
                <Box sx={{ px: 2, py: 1 }}>
                  <Box display="flex" alignItems="center" gap={0.75} mb={0.75}>
                    <SwapVertIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <SummaryLabel>Valeur des ajustements</SummaryLabel>
                  </Box>
                  {valeurInventaire.nbBoni === 0 && valeurInventaire.nbMali === 0 ? (
                    <Typography variant="body2" color="text.secondary">Inventaire conforme — aucun écart.</Typography>
                  ) : (
                    <>
                      {valeurInventaire.nbBoni > 0 && (
                        <SummaryRow
                          label={`Excédents (${valeurInventaire.nbBoni})`}
                          value={valeurInventaire.valeurBoni}
                          color="success.main"
                        />
                      )}
                      {valeurInventaire.nbMali > 0 && (
                        <SummaryRow
                          label={`Manquants (${valeurInventaire.nbMali})`}
                          value={-valeurInventaire.valeurMali}
                          color="error.main"
                        />
                      )}
                      <Divider sx={{ my: 0.5 }} />
                      <SummaryRow
                        label="Ajustement net"
                        value={valeurInventaire.valeurNette}
                        bold
                        color={valeurInventaire.valeurNette >= 0 ? 'success.main' : 'error.main'}
                      />
                    </>
                  )}
                </Box>
              </>
            )}

            {/* Total général */}
            <Box
              sx={{
                px: 2,
                py: 1.25,
                background: 'linear-gradient(90deg, #1976d2 0%, #1565c0 100%)',
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box display="flex" alignItems="center" gap={0.75}>
                  <CallMadeIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.8)' }} />
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#fff', letterSpacing: '0.02em' }}>
                    {isInventaire ? 'Valeur du stock compté' : 'Total général TTC'}
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 800, color: '#fff', fontVariantNumeric: 'tabular-nums', letterSpacing: '0.01em' }}>
                  {formatNumberWithSpaces(totalGeneral)}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      )}

      {/* ── Documents liés ── */}
      {childDocs.length > 0 && (
        <Paper elevation={0} sx={{ mt: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
          {/* En-tête section */}
          <Box
            sx={{
              px: 2,
              py: 1.25,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: 'action.hover',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <LinkIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary', flex: 1 }}>
              Documents liés
            </Typography>
            <Chip label={childDocs.length} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
          </Box>

          <Table size="small">
            <TableHead>
              <TableRow>
                {['N° Document', 'Type', 'Date', 'Tiers', 'Statut', 'Total TTC'].map((col, i) => (
                  <TableCell
                    key={col}
                    align={i === 5 ? 'right' : 'left'}
                    sx={{
                      bgcolor: 'grey.50',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      color: 'text.secondary',
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                      py: 1,
                    }}
                  >
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {childDocs.map((doc, idx) => (
                <TableRow
                  key={doc.id}
                  onClick={() => dispatch(setSelectedDocument(doc))}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: idx % 2 !== 0 ? 'rgba(0,0,0,0.015)' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' },
                    '&:last-child td': { border: 0 },
                    transition: 'background-color 0.15s ease',
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '0.8rem' }}>
                      {doc.numero_doc}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={doc.type_document?.libelle ?? doc.doc_code ?? '—'}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: 20 }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                    {doc.date_doc ? new Date(doc.date_doc).toLocaleDateString('fr-FR') : '—'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 160, fontSize: '0.8rem' }}>
                      {doc.tiers ? `${doc.tiers.nom}${doc.tiers.prenom ? ' ' + doc.tiers.prenom : ''}` : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {doc.statut
                      ? <Chip label={doc.statut.libelle} size="small" sx={{ bgcolor: doc.statut.couleur ?? undefined, color: '#fff', fontWeight: 600, fontSize: '0.7rem', height: 20 }} />
                      : <Typography variant="body2" color="text.disabled">—</Typography>
                    }
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                      {formatNumberWithSpaces(doc.total_ttc ?? null)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
};

export default LigneDocumentVente;
